import {
  StateToText,
  ClaimTypeToText,
  formatPrice,
  formatDate,
  humanizeCount,
  ResolutionTypeToText,
  formatDate2,
} from '../../../helpers';

import { REFUND, RESHIP, CUSTOM, STORE_CREDIT, FLAT_RATE } from '@pulse/constants/claims';

import { BaseModel } from './base.model';
import { ClaimItemModel } from './claim-item.model';
import { ClaimResolutionModel } from './claim-resolution.model';
import countBy from 'lodash/countBy';
import { ClaimCategory } from '@monolog/shared/types';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
dayjs.extend(duration);

export class ClaimModel extends BaseModel<any> {
  get id() {
    return this._data.id;
  }

  get documents() {
    return this._data.claimDocuments;
  }

  get uploadedPhotos() {
    return this._data.claimItems.flatMap((item: any) => item.images);
  }

  get productImages() {
    return this._data.claimItems.flatMap((item: any) => item?.imageUrl).filter(Boolean);
  }

  get warrantyValue() {
    return formatPrice(this._data.warrantyMetadata?.productValue || 0, this._data.policy?.premiumCurrency || 'USD');
  }

  get warrantyFee() {
    return formatPrice(this._data.warrantyMetadata?.warrantyFee, 'USD');
  }

  get warrantyValidUntil() {
    const dateOfPurchase = this._data.warrantyMetadata?.dateOfPurchase;
    const warrantyPeriod = this._data.warrantyMetadata?.warrantyPeriod;

    try {
      const parsedDuration = dayjs.duration(warrantyPeriod);
      const endDate = dayjs(dateOfPurchase).add(parsedDuration);

      return endDate.format('MMM D, YYYY');
    } catch (error) {
      return 'Invalid warranty period';
    }
  }

  get claimCategory() {
    return this._data.claimCategory as ClaimCategory;
  }

  get claimSubtype() {
    return this._data.claimSubtype;
  }

  get userId() {
    return this._data.userId;
  }

  get printableId() {
    return this._data.sourceOrderNumber;
  }

  get order() {
    return this._data.order;
  }

  get sourceOrderId() {
    return this._data.sourceOrderId;
  }

  get sourceOrderNumber() {
    return this._data.sourceOrderNumber;
  }

  get orderId() {
    return this._data.orderId;
  }

  get claimDescription() {
    return this._data.claimDescription;
  }

  get stateText() {
    return StateToText[this._data.claimState] || 'Unknown';
  }

  get state() {
    return this._data.claimState?.toLowerCase();
  }

  get rawState() {
    return this._data.claimState;
  }

  get isResolvable() {
    return this.resolutions.length === 0;
  }

  get hasApprovedResolution() {
    return this.resolutions.some((r: ClaimResolutionModel) =>
      [REFUND, RESHIP, STORE_CREDIT, FLAT_RATE, CUSTOM].includes(r.providedResolution),
    );
  }

  get salesChannel() {
    return this._data.salesChannel;
  }

  get warrantyMetadata() {
    return this._data.warrantyMetadata;
  }

  get warrantyDuration() {
    return this._data.warrantyMetadata?.warrantyPeriod;
  }

  get warrantyProductDescription() {
    return this._data.warrantyMetadata?.productDescription;
  }

  get warrantyDateOfPurchase() {
    return this._data.warrantyMetadata?.dateOfPurchase;
  }

  get stateBadge() {
    switch (this.state) {
      case 'new':
        return 'info';
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'denied':
        return 'danger';
      case 'dismissed':
        return 'neutral';
      case 'closed':
        return 'neutral';
      case 'draft':
        return 'warning';
      default:
        return 'info';
    }
  }

  get protectionStatus() {
    if (this.isOta) return 'ota';

    if (this._data.policy?.removedAt) return 'removed';

    return this._data.policy !== null ? 'protected' : 'unprotected';
  }

  get isOta() {
    return this._data.isOta;
  }

  get author() {
    return this._data.authorName;
  }

  get customer() {
    return this.author;
  }

  get authorEmail() {
    return this._data.authorEmail;
  }

  get store() {
    return this._data.storeId || "Leo's";
  }

  get total() {
    return formatPrice(this._data.policy?.insuredAmount || 0, this._data.policy?.premiumCurrency || 'USD');
  }

  get description() {
    return this._data.claimDescription;
  }

  get protectionCost() {
    return formatPrice(this._data.policy?.premiumPaid || 0, this._data.policy?.premiumCurrency || 'USD');
  }

  get date() {
    return formatDate(this._data.createdAt);
  }

  get dateV2() {
    return formatDate2(this._data.createdAt);
  }

  get updatedAt() {
    return formatDate(this._data.updatedAt);
  }

  get type() {
    return 'shipping_claim';
  }

  get storeCredits() {
    return this._data.claimStoreCredits;
  }

  set storeCredits(storeCredit) {
    this._data.claimStoreCredits = storeCredit;
  }

  get resolutionType() {
    return this.resolutions[0].type;
  }

  get resolutionTypeForCustomer() {
    return this.resolutions[0].typeForCustomer;
  }

  get claimType() {
    return ClaimTypeToText[this._data.claimType];
  }

  get rawClaimType() {
    return this._data.claimType;
  }

  set rawClaimType(claimType) {
    this._data.claimType = claimType;
  }

  get authorName() {
    return this._data.authorName;
  }

  get denialReason() {
    return ResolutionTypeToText[this._data.claimResolutions[0]?.resolutionType];
  }

  get claimStateBadge() {
    const typeMap = {
      approved: 'success',
      closed: 'neutral',
      denied: 'danger',
      dismissed: 'neutral',
      fulfilled: 'neutral',
      open: 'primary',
      payment: 'neutral',
      unfulfilled: 'neutral',
    } as any;

    return typeMap[this.state.toLowerCase()] || 'neutral';
  }

  get claimTypeBadge() {
    return 'danger';
  }

  get url() {
    return `/claims/view/${this.orderId}`;
  }

  get resolutions() {
    return ClaimResolutionModel.fromArrayExtending(this._data.claimResolutions, {
      claim: this,
    }) as ClaimResolutionModel[];
  }

  get resolution() {
    return this.resolutions[0];
  }

  get resolutionRequested() {
    const resolutions = countBy(this.claimItems, 'resolutionRequested');

    if (Object.keys(resolutions).length > 1) {
      return CUSTOM;
    } else if (resolutions[REFUND] > 0) {
      return REFUND;
    } else if (resolutions[RESHIP] > 0) {
      return RESHIP;
    } else if (resolutions[STORE_CREDIT] > 0) {
      return STORE_CREDIT;
    }

    return null;
  }

  get providedResolution() {
    return this.resolutions[0]?.providedResolution;
  }

  get creditStore() {
    return this.resolutions.find((r: any) => r.isCreditStore);
  }

  get refundShipping() {
    return this.resolutions.find((r: any) => r.isRefundShipping);
  }

  get replacementOrder() {
    return this.resolutions.find((r: any) => r.isReplacementOrder);
  }

  get details() {
    return {
      lineItems: [],
    };
  }

  get subtotal() {
    return this.claimItems
      .reduce((total, item) => total + parseFloat((item.price - item.discount) * item.validQuantity * exchangeRate), 0)
      .toFixed(2);
  }

  get currency() {
    return this.order.currency;
  }

  get shippingCost() {
    return this.order.shippingCost;
  }
  get shippingCostTax() {
    return this.order.shippingCostTax;
  }

  get totalValue() {
    const lineItemsSum = this.claimItems.reduce((total, item) => {
      return total + parseFloat(item.price) * item.quantity; // Multiply the amount by the quantity
    }, 0);

    const taxesAmount = parseFloat(this.order.tax);
    const shippingAmount = parseFloat(this.order.shippingCost);
    const totalAmount = lineItemsSum + taxesAmount + shippingAmount;
    const totalAmountInUsd = totalAmount * exchangeRate;

    return totalAmountInUsd.toFixed(2);
  }

  get claimItems() {
    return ClaimItemModel.fromArrayExtending(this._data.claimItems, {
      claim: this,
    }) as ClaimItemModel[];
  }

  get messages() {
    return this._data.claimMessages;
  }

  get itemsCount() {
    return this._data.claimItems?.length || this._data._count?.claimItems || 0;
  }

  get exchangeRate() {
    return this.order.exchangeRate;
  }

  get itemsCountHumanized() {
    return humanizeCount(this.itemsCount, 'item', 'items');
  }

  get policyId() {
    return this._data.policy?.id;
  }

  get pricingModel() {
    return this._data.policy?.pricingModel;
  }

  set messages(messages) {
    this._data.claimMessages = messages;
  }
}
